import {useState, useEffect, useRef, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {MIN_ARTS_TO_CREATE_CAMPAIGN} from '../../../../constants/campaignCreation';
import {notifyErrorCustom} from '../../../../utils/notify';
import {ensureCampaignCreationAllowed} from '../../utils/ensureCampaignCreationAllowed';
import HamburgerIcon from '../../../../components/SidebarHistoricoCampanhas/HamburgerIcon';
import ArtLimitProgress from '../../../../components/ArtLimitProgress';
import upgrade from '../../../../assets/logos/upgrade.svg';
import useAgentSessionStore from '../../store/agentSessionStore';
import {useAgentChatContext} from '../../context/AgentChatContext';
import {listSessions, deleteSession} from '../../services/agentSessionService';
import useArtAvailability from '../../hooks/useArtAvailability';
import {useAutoHideScrollbar} from '../../hooks/useAutoHideScrollbar';
import styles from './styles.module.css';

const SESSION_STATUS_KEYS = {
  active: 'campaignChat.status.active',
  completed: 'campaignChat.status.completed',
};

export default function Sidebar() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {
    sessions,
    activeSessionId,
    isLoading,
    hasMore,
    setSessions,
    setActiveSession,
    setIsLoading,
    setHasMore,
    appendSessions,
  } = useAgentSessionStore();
  const {setMessages} = useAgentChatContext();

  const [expanded, setExpanded] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const {usedArts: artesCriadas, totalArts: limiteArtes, availableArts} =
    useArtAvailability();
  const listRef = useRef(null);
  const {ref: historyScrollRef, scrollClassName: historyScrollClassName} =
    useAutoHideScrollbar(listRef);
  const loadingMoreRef = useRef(false);

  const newConversationLabel = t('campaignChat.newConversation');

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchSessions = useCallback(
    async (search = '', cursor = '') => {
      setIsLoading(true);
      try {
        const result = await listSessions(search, cursor);
        if (cursor) {
          appendSessions(result.sessions || []);
        } else {
          setSessions(result.sessions || []);
        }
        setHasMore(result.hasMore || false);
      } catch (err) {
        console.error('Error fetching sessions:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [appendSessions, setHasMore, setIsLoading, setSessions],
  );

  useEffect(() => {
    fetchSessions(debouncedQuery);
  }, [debouncedQuery, fetchSessions]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => {
      if (!hasMore || isLoading || loadingMoreRef.current) return;

      const {scrollTop, clientHeight, scrollHeight} = el;
      if (scrollTop + clientHeight >= scrollHeight - 120) {
        const lastSession = sessions[sessions.length - 1];
        if (!lastSession?.createdAt) return;

        loadingMoreRef.current = true;
        fetchSessions(debouncedQuery, lastSession.createdAt).finally(() => {
          setTimeout(() => {
            loadingMoreRef.current = false;
          }, 300);
        });
      }
    };

    el.addEventListener('scroll', onScroll, {passive: true});
    return () => el.removeEventListener('scroll', onScroll);
  }, [debouncedQuery, fetchSessions, hasMore, isLoading, sessions]);

  const isCampaignCreationBlocked =
    availableArts !== null && availableArts < MIN_ARTS_TO_CREATE_CAMPAIGN;

  const handleNewConversation = async () => {
    if (isCampaignCreationBlocked) {
      notifyErrorCustom(
        t('campaign_history_sidebar.insufficient_arts_tooltip', {
          min: MIN_ARTS_TO_CREATE_CAMPAIGN,
        }),
      );
      return;
    }

    const allowed = await ensureCampaignCreationAllowed();
    if (!allowed) {
      notifyErrorCustom(t('campaignChat.errors.insufficientArtsToStart'));
      return;
    }

    setActiveSession(null);
    setMessages([]);
  };

  const handleSelectSession = (sessionId) => {
    setActiveSession(sessionId);
  };

  const handleDeleteSession = async (event, sessionId) => {
    event.stopPropagation();
    if (!window.confirm(t('campaignChat.sidebar.deleteConfirm', 'Excluir esta conversa?'))) {
      return;
    }
    try {
      await deleteSession(sessionId);
      if (activeSessionId === sessionId) {
        setActiveSession(null);
        setMessages([]);
      }
      fetchSessions(debouncedQuery);
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  const getStatusBadge = (status) => {
    const statusKey = SESSION_STATUS_KEYS[status] || SESSION_STATUS_KEYS.active;
    const className =
      status === 'completed' ? styles.badgeCompleted : styles.badgeActive;

    return (
      <span className={`${styles.badge} ${className}`}>
        {t(statusKey)}
      </span>
    );
  };

  return (
    <aside
      className={`${styles.sidebar} ${expanded ? '' : styles.sidebarCollapsed}`}
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.hamburgerBtn}
          onClick={() => setExpanded((prev) => !prev)}
          aria-label={
            expanded
              ? t('campaign_history_sidebar.collapse_menu')
              : t('campaign_history_sidebar.expand_menu')
          }
        >
          <HamburgerIcon color="#414141" size={20} />
        </button>
      </div>

      <div
        className={`${styles.content} ${
          expanded ? styles.contentExpanded : styles.contentCollapsed
        }`}
        aria-hidden={!expanded}
      >
        <ArtLimitProgress
          artesCriadas={artesCriadas}
          limiteArtes={limiteArtes}
          layout="sidebar"
        />

        <div className={styles.historySection}>
          <button
            type="button"
            className={styles.historyHeader}
            onClick={() => setIsHistoryOpen((prev) => !prev)}
          >
            <div className={styles.sectionDot} />
            <span className={styles.sectionTitle}>
              {t('campaign_history_sidebar.section_title')}
            </span>
            <span
              className={`${styles.chevron} ${
                isHistoryOpen ? styles.chevronOpen : ''
              }`}
            >
              <svg
                width="14"
                height="8"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.37629 7.37629C7.18876 7.56376 6.93445 7.66907 6.66929 7.66907C6.40412 7.66907 6.14982 7.56376 5.96229 7.37629L0.305288 1.71929C0.209778 1.62704 0.133596 1.5167 0.0811869 1.39469C0.0287779 1.27269 0.00119157 1.14147 3.77564e-05 1.00869C-0.00111606 0.87591 0.0241859 0.744231 0.0744668 0.621335C0.124748 0.498438 0.199001 0.386786 0.292893 0.292893C0.386786 0.199 0.498438 0.124747 0.621334 0.0744663C0.74423 0.0241854 0.87591 -0.00111606 1.00869 3.77571e-05C1.14147 0.00119157 1.27269 0.0287779 1.39469 0.0811869C1.5167 0.133596 1.62704 0.209778 1.71929 0.305288L6.66929 5.25529L11.6193 0.305288C11.8079 0.12313 12.0605 0.0223355 12.3227 0.0246139C12.5849 0.0268924 12.8357 0.132061 13.0211 0.317469C13.2065 0.502877 13.3117 0.75369 13.314 1.01589C13.3162 1.27808 13.2154 1.53069 13.0333 1.71929L7.37629 7.37629Z"
                  fill="#902ABF"
                  fillOpacity="0.7"
                />
              </svg>
            </span>
          </button>

          {isHistoryOpen && (
            <div className={styles.historyContent}>
              <div className={styles.searchContainer}>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder={t('campaign_history_sidebar.search_placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label={t('campaign_history_sidebar.search_aria')}
                />
                <div className={styles.searchIcon}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20.75 22.5L12.875 14.625C12.25 15.125 11.5313 15.5208 10.7188 15.8125C9.90625 16.1042 9.04167 16.25 8.125 16.25C5.85417 16.25 3.9325 15.4633 2.36 13.89C0.787501 12.3167 0.000833995 10.395 6.61376e-07 8.125C-0.000832672 5.855 0.785834 3.93333 2.36 2.36C3.93417 0.786667 5.85583 0 8.125 0C10.3942 0 12.3163 0.786667 13.8913 2.36C15.4663 3.93333 16.2525 5.855 16.25 8.125C16.25 9.04167 16.1042 9.90625 15.8125 10.7188C15.5208 11.5312 15.125 12.25 14.625 12.875L22.5 20.75L20.75 22.5ZM8.125 13.75C9.6875 13.75 11.0158 13.2033 12.11 12.11C13.2042 11.0167 13.7508 9.68833 13.75 8.125C13.7492 6.56167 13.2025 5.23375 12.11 4.14125C11.0175 3.04875 9.68917 2.50167 8.125 2.5C6.56083 2.49833 5.23292 3.04542 4.14125 4.14125C3.04958 5.23708 2.5025 6.565 2.5 8.125C2.4975 9.685 3.04458 11.0133 4.14125 12.11C5.23792 13.2067 6.56583 13.7533 8.125 13.75Z"
                      fill="#878787"
                    />
                  </svg>
                </div>
                {searchQuery && (
                  <button
                    type="button"
                    className={styles.searchClear}
                    onClick={() => setSearchQuery('')}
                    aria-label={t('campaign_history_sidebar.clear_search')}
                  >
                    ×
                  </button>
                )}
              </div>

              <div
                ref={historyScrollRef}
                className={`${styles.scrollContainer} ${historyScrollClassName}`}
              >
                <ul className={styles.list}>
                  <li
                    className={`${styles.item} ${styles.itemNewConversation}${
                      isCampaignCreationBlocked ? ` ${styles.itemDisabled}` : ''
                    }`}
                    onClick={handleNewConversation}
                    title={
                      isCampaignCreationBlocked
                        ? t('campaign_history_sidebar.insufficient_arts_tooltip', {
                            min: MIN_ARTS_TO_CREATE_CAMPAIGN,
                          })
                        : undefined
                    }
                  >
                    <div className={styles.itemInfo}>
                      <div className={styles.itemHeaderRow}>
                        <span className={styles.itemTitle}>
                          {newConversationLabel}
                        </span>
                        <div className={styles.newItemIcon}>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#9002CD"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </li>

                  {sessions.map((session) => (
                    <li
                      key={session.uuid}
                      className={`${styles.item} ${
                        activeSessionId === session.uuid
                          ? styles.itemSelected
                          : ''
                      }`}
                      onClick={() => handleSelectSession(session.uuid)}
                    >
                      <div className={styles.itemInfo}>
                        <div className={styles.itemHeaderRow}>
                          <span className={styles.itemTitle}>
                            {session.title || newConversationLabel}
                          </span>
                          <div className={styles.itemActions}>
                            {getStatusBadge(session.status)}
                            <button
                              type="button"
                              className={styles.deleteBtn}
                              aria-label={t('campaignChat.sidebar.delete', 'Excluir conversa')}
                              onClick={(event) =>
                                handleDeleteSession(event, session.uuid)
                              }
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {!isLoading && sessions.length === 0 && !searchQuery && (
                  <div className={styles.emptyState}>
                    <p className={styles.emptyStateText}>
                      {t('campaignChat.sidebar.empty')}
                    </p>
                    <p className={styles.emptyStateSubtext}>
                      {t('campaignChat.sidebar.emptyHint')}
                    </p>
                  </div>
                )}
                {!isLoading && sessions.length === 0 && searchQuery && (
                  <div className={styles.emptyState}>
                    <p className={styles.emptyStateText}>
                      {t('campaignChat.sidebar.emptySearch')}
                    </p>
                  </div>
                )}

                {isLoading && (
                  <div className={styles.loadingIndicator}>
                    {t('campaign_history_sidebar.loading')}
                  </div>
                )}

                <div className={styles.listFade} />
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          className={styles.upgradeBtn}
          onClick={() => navigate(CAMPAIGN_CHECKOUT_ROUTE)}
        >
          <img src={upgrade} alt="Logo" className={styles.upgradeIcon} />
          {t('campaign_history_sidebar.upgrade_plan')}
        </button>
      </div>
    </aside>
  );
}
